/**
 * Sidebar access filtering, kept pure so the rules are testable without
 * rendering the app shell.
 */

/**
 * Pages every signed-in user may open, whatever their `custom:permissions`.
 * Briefs belong to the organisation (zoe_curated_briefs.md §9 D3): anyone may
 * read them; the server still hides each section the reader's pages don't
 * cover, and changing a brief needs `zoe-briefs`.
 */
export const OPEN_PAGES: ReadonlySet<string> = new Set(['zoe-briefs']);

export interface AccessTab {
  id: string;
  useCase?: 'logistics' | 'emergency' | 'both';
  children?: AccessTab[];
}

/**
 * Section parents (Optimisation, Fleet Report, …): without parent permission
 * the whole group is hidden — never a greyed header because a child is still
 * allowed. An open page inside a hidden group (Briefs under ZOE) is lifted to
 * the top level instead, so reaching it never opens the group's own page.
 */
export function filterTabsByAccess<T extends AccessTab>(
  tabs: T[],
  allowed: (tab: T) => boolean,
): T[] {
  const filtered: T[] = [];
  for (const tab of tabs) {
    if (!allowed(tab)) {
      const open = ((tab.children ?? []) as T[]).filter((c) => OPEN_PAGES.has(c.id) && allowed(c));
      filtered.push(...open.map((c) => ({ ...c, children: undefined })));
      continue;
    }
    const children = tab.children ? filterTabsByAccess(tab.children as T[], allowed) : undefined;
    filtered.push({ ...tab, children });
  }
  return filtered;
}
